import { z } from "zod";
import { Instant, LocalDate, LocalTime, Timezone } from "./time.js";
import { RedactionLevel } from "./redaction.js";
import { Persona } from "./persona.js";
import { EscalationLadder } from "./escalation.js";
import { ScriptSkeleton } from "./script.js";
/** Current contract version. Bump on breaking shape changes; worker checks it. */
export const CONTRACT_VERSION = 1;
export const PhoneNumbers = z.object({
    /** E.164, e.g. "+13055551234". */
    primary: z.string().regex(/^\+\d{7,15}$/, "expected E.164"),
    /** Called if primary unanswered twice (§9). */
    backups: z.array(z.string().regex(/^\+\d{7,15}$/)).default([]),
});
export const WakeConfig = z.object({
    timezone: Timezone,
    /** Computed T₀ — first call fires here. Absolute instant. */
    wakeTime: Instant,
    /** Computed Hard Wake — continuous calls begin here. Absolute instant. */
    hardWake: Instant,
    /** Human-readable derivation for the PWA "computed never means opaque" rule. */
    wakeExplanation: z
        .string()
        .describe('e.g. "6:45 = 9:20 leave-by − 34m drive − 8m parking − 75m routine"'),
    phones: PhoneNumbers,
    persona: Persona,
    /** Redaction level the brain applied; recorded for audit. */
    redactionLevel: RedactionLevel,
});
/**
 * THE CONTRACT. Pushed nightly (≈2 AM) from the brain to the cloud worker over
 * an authenticated channel. The worker consumes ONLY this — no platform APIs,
 * no calls home at T₀ — which is exactly what lets the brain move between cloud
 * and Pi "with zero worker refactor" (§7.4 escape hatch).
 */
export const CallPayload = z.object({
    contractVersion: z.literal(CONTRACT_VERSION),
    date: LocalDate,
    script: ScriptSkeleton,
    wake: WakeConfig,
    escalation: EscalationLadder,
    /** When the brain compiled this. */
    generatedAt: Instant,
    /** Worker purges at/after this (cloud retains nothing past 7 days). */
    expiresAt: Instant,
    /**
     * True when this is the generic fallback (nightly push failed, see
     * `makeFallbackPayload`). The worker still rings; it just doesn't know the day.
     */
    isFallback: z.boolean().default(false),
});
/**
 * The fallback script (§2 key behaviors). When the nightly push fails, the
 * worker arms a generic payload so the alarm function never depends on the
 * assistant function. Takes the last-known wake config so it still rings on time.
 */
export function makeFallbackPayload(opts) {
    const echoTime = opts.approxLeaveBy ?? "now";
    return {
        contractVersion: CONTRACT_VERSION,
        date: opts.date,
        isFallback: true,
        generatedAt: opts.generatedAt,
        expiresAt: opts.expiresAt,
        wake: opts.wake,
        escalation: opts.escalation,
        script: {
            greeting: "Morning. I couldn't reach base last night so I don't have your full day — but it's time to get up.",
            weather: null,
            beats: ["Check the app once you're vertical; I'll have the real plan there."],
            firstEvent: null,
            prepBeforeNoon: [],
            taskEcho: {
                prompt: "I need you to actually say something so I know you're awake.",
                challenge: "Tell me one thing you have to do today.",
                expectedAnswer: echoTime,
                acceptablePhrases: [],
            },
            firstAction: { text: "Feet on floor, water, then open the app." },
        },
    };
}
/** Envelope the brain pushes; carries auth metadata around the payload. */
export const PushEnvelope = z.object({
    payload: CallPayload,
    /** Monotonic counter so the worker rejects replays / stale pushes. */
    sequence: z.number().int().min(0),
});
//# sourceMappingURL=payload.js.map